<template lang="pug">
Field.select(
  :required="Boolean(required)"
  :label="label"
  :errors="!touched ? [] : errors"
  :for="id || null"
)
  span.block.select-wrap
    select(
      v-bind="bindAttrs"
      :value="modelValue"
      @change="update($event)"
      :class="[{\
        'input-touched': touched,\
        'input-error': errors.length > 0,\
        'input-disabled': !selectedOption\
      }, color]"
      @blur="touched = true;"
      @reset="touched = false;"
      :disabled="disabled"
    )
      option(
        v-if="disabledOptionText"
        value=""
        :selected="!selectedOption"
        disabled
      ) {{ disabledOptionText }}
      option(
        v-for="opt in options"
        :disabled="opt.disabled"
        :key="opt.label"
        :value="opt.value"
        :selected="selectedOption === opt"
      ) {{ __(opt.label) }}
    Icon.icon.icon-size-xxs(name="triangle")
  slot(name="helper")
</template>

<script setup lang="ts">
import { ref, computed, watch, useAttrs } from 'vue'
import { useRogue } from '../../js/rogue'

type Option = { value: string | number | boolean | null | undefined, label: string | number, disabled?: boolean }

type Props = {
  options?: Option[]
  modelValue?: string | number | boolean
  label?: string | null
  id?: string
  required?: boolean | string
  error?: string
  disabledOptionText?: string
  color?: 'flat' | 'gray' | string
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  options            : () => [],
  modelValue         : '',
  label              : null,
  id                 : '',
  required           : false,
  error              : '',
  disabledOptionText : '',
  color              : 'gray',
  disabled           : false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | boolean | null | undefined): void
}>()

const attrs = useAttrs()
const touched = ref<boolean>(false)
const errors = ref<string[]>([])
const messages = ref<string[]>([])

const bindAttrs = computed(() => {
  const { class: _class, style: _style, label, disabledOptionText, options, ...rest } = attrs as any
  if (props.id) {
    (rest as any).id = props.id
  }
  
  return rest
})

const selectedOption = computed<Option | undefined>(() => props.options.find((opt) => opt.value === props.modelValue))

watch(selectedOption, () => validate())
watch(() => props.options, () => validate(), { deep: true })
watch(() => props.error, () => validate())

const rogue = useRogue()

function update(event: Event) {
  const target = event.target as HTMLSelectElement
  const selected = props.options.find((o) => {
    const optVal = ((o.value === null || o.value === undefined) ? o.value : o.value.toString())

    return optVal === target.value
  })
  emit('update:modelValue', selected ? selected.value : null)
}

function validate() {
  if (props.required && !selectedOption.value) {
    if (typeof props.required === 'string' && props.required !== 'true') {
      errors.value = [props.required]
    } else if (props.label) {
      errors.value = [rogue.__('Please select your {fieldName}', { fieldName: props.label.toLowerCase() })]
    } else {
      errors.value = [rogue.__('Please select an option')]
    }
  } else if (props.error) {
    errors.value = [rogue.__(props.error)]
  } else {
    errors.value = []
  }
}

validate()
</script>

<style lang="scss" scoped>

  .select-wrap {
    position: relative;
    margin: var(--select-wrap-margin, 0);

    .icon {
      position: absolute;
      bottom: $size-xs;
      right: $size-xxs;
      pointer-events: none;
      color: var(--select-icon-color, revert);
    }

  }

  select {
    font-family: $font-sans-serif;
    font-size: var(--select-font-size, #{$size-xs});
    color: var(--select-color, #{$color-black});
    display: block;
    width: 100%;
    padding: var(--select-padding, #{$space-xs} #{$space-l} #{$space-xs} #{$space-xs});
    border: var(--select-border, 1px solid #{$color-gray});
    border-radius: var(--select-border-radius, #{$radius-default});
    background: var(--select-background, linear-gradient(180deg, #{$color-white} 0%, #{$color-gray-light} 100%));
    cursor: pointer;

    appearance: none;
    -moz-appearance: none;
    -webkit-appearance: none;

    &.input-error.input-touched {
      border: 1px solid $color-red;
    }

    &:hover,
    &:active {
      background: var(--select-background, linear-gradient(180deg, #{$color-white} 0%, #{$color-gray-light} 100%));
    }

    &::-ms-expand {
      display: none;
    }

    &.flat {
      background: $color-white;
      border-radius: 0;
      font-family: $font-base;
    }
  }

  @supports (-webkit-overflow-scrolling: touch) {
    select {
      font-size: var(--select-font-size-touch, #{$size-s});
      padding-top: 11px;
      padding-bottom: 11px;
    }
  }

</style>

<style lang="scss">
  /* Dark mode styles - must be in non-scoped style block to access parent selectors */
  [data-theme='dark'] .select-wrap,
  .dark-mode .select-wrap {
    select {
      background: $color-black-light;
      border-color: $color-black-light;
      color: $color-white;

      &:hover,
      &:active {
        background: $color-black-light;
      }

      &.flat {
        background: $color-black-light;
      }
    }

    .icon {
      color: $color-gray-dark;
    }
  }
</style>
